import { useCallback, useEffect, useMemo, useState } from 'react';
import { track } from '../_core/common/eventTracking/tracker';
import { useUPG6101Experiment } from '../experiments/useUpg6101Experiment';
import { logRavenException } from '../_core/utils/errors';
import { openUpgradePageInNewTab } from '../_core/utils/upgradePageRedirect';
import { UpgradePointTooltip } from './UpgradePointTooltip';
import useErrorAlert from './useErrorAlert';
import { useUpgradePointTooltipData } from './useUpgradePointTooltipData';
import { VALUE_PROP_MODAL_CONFIGS } from './ValuePropModal/constants';
import ValuePropModalContent from './ValuePropModal/ValuePropModalContent';
import { getUpgradeConfigTrackingProperties, getUpgradePointPromise, updateViewedUpgradePointsCache, warnArchivedUpgradePointUse } from './utils';

/**
 * ## Upgrade Management System
 *
 * `useUpgradePoint` takes your upgrade key as an argument and returns a callback
 * to open the associated upgrade page. Upgrade keys can be created and managed in the
 * [Upgrade Management System](<https://tools.hubteamqa.com/ums>) (`go/ums`).
 *
 * @example
 * ```tsx
 * const YourComponent = () => {
 *  const { openUpgradePage } = useUpgradePoint('your-upgrade-key');
 *
 *  return <Button onClick={openUpgradePage} />;
 * }
 * ```
 */
import { jsx as _jsx } from "react/jsx-runtime";
export function useUpgradePoint(upgradeKey, isFeatureLocked = true) {
  const {
    isUPG6101Variation,
    isUPG6101Fetched
  } = useUPG6101Experiment();
  const {
    setShowErrorAlert
  } = useErrorAlert();
  const upgradePointPromise = useMemo(() => {
    return getUpgradePointPromise({
      upgradeKey
    });
  }, [upgradeKey]);

  // Fetch upgrade config and track view
  useEffect(() => {
    if (isFeatureLocked && upgradeKey) {
      upgradePointPromise.then(upgradeConfig => {
        if (!upgradeConfig) {
          return;
        }
        updateViewedUpgradePointsCache(upgradeKey, () => {
          track('interaction', Object.assign({
            action: 'viewed upgrade point'
          }, getUpgradeConfigTrackingProperties(upgradeConfig)));
          if (upgradeConfig.status === 'ARCHIVED') {
            warnArchivedUpgradePointUse(upgradeConfig.upgradeKey);
          }
        });
      }).catch(err => {
        console.error(err);
      });
    }
  }, [isFeatureLocked, upgradeKey, upgradePointPromise]);

  // Track click and set state
  const openUpgradePage = useCallback(() => {
    try {
      if (!isFeatureLocked) {
        return;
      }
      openUpgradePageInNewTab(upgradeKey);
      upgradePointPromise.then(upgradeConfig => {
        if (!upgradeConfig) return;
        track('interaction', Object.assign({
          action: 'clicked upgrade point'
        }, getUpgradeConfigTrackingProperties(upgradeConfig)));
      }).catch(__error => {
        setShowErrorAlert(true);
      });
    } catch (error) {
      setShowErrorAlert(true);
      logRavenException({
        customMessage: 'useUpgradePoint tryCatch error: An error occured in defining openUpgradePage',
        error,
        uniqueId: upgradeKey
      });
    }
  }, [isFeatureLocked, setShowErrorAlert, upgradeKey, upgradePointPromise]);
  const UpgradePointTooltipWithUpgradeKey = useMemo(() => ({
    children
  }) => /*#__PURE__*/_jsx(UpgradePointTooltip, {
    upgradeKey: upgradeKey,
    enableTooltip: isFeatureLocked,
    children: children
  }), [upgradeKey, isFeatureLocked]);
  const upgradePointTooltipData = useUpgradePointTooltipData(upgradeKey, isFeatureLocked);
  const [isValuePropModalOpen, setIsValuePropModalOpen] = useState(false);
  const valuePropModalConfig = VALUE_PROP_MODAL_CONFIGS[upgradeKey];
  const openValuePropModal = useCallback(() => {
    if (!isUPG6101Fetched) {
      return;
    }
    if (valuePropModalConfig && isUPG6101Variation) {
      setIsValuePropModalOpen(true);
    } else {
      openUpgradePage();
    }
  }, [valuePropModalConfig, isUPG6101Fetched, isUPG6101Variation, openUpgradePage]);
  const ValuePropModalWithUpgradeKey = useMemo(() => () => isValuePropModalOpen && valuePropModalConfig ? /*#__PURE__*/_jsx(ValuePropModalContent, {
    config: valuePropModalConfig,
    onClose: () => setIsValuePropModalOpen(false)
  }) : null, [isValuePropModalOpen, valuePropModalConfig]);
  return {
    openUpgradePage,
    openValuePropModal,
    UpgradePointTooltip: UpgradePointTooltipWithUpgradeKey,
    ValuePropModal: ValuePropModalWithUpgradeKey,
    upgradePointTooltipData
  };
}